import { BookOpen, Check, HelpCircle, Megaphone, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getLearningCenterBaseUrl, getLearningCenterUrl } from '../constants/learning-center';
import { useAuth } from '../contexts/auth-context';
import { attorneyTours, doctorTours, type TourDefinition } from '../tours';
import { safeGetItem, safeRemoveItem } from '../utils/safe-storage';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface TourHelpButtonProps {
  userRole: 'doctor' | 'attorney';
  onNavigate?: (route: string) => void;
}

function isTourCompleted(tourId: string, userId: string): boolean {
  return !!safeGetItem(`expertmd_tour_${tourId}_${userId}`);
}

function clearAllTours(tours: TourDefinition[], userId: string) {
  tours.forEach((t) => {
    safeRemoveItem(`expertmd_tour_${t.id}_${userId}`);
  });
}

export function TourHelpButton({ userRole, onNavigate }: TourHelpButtonProps) {
  const { user } = useAuth();
  const userId = user?.id || user?.userId || '';

  // [FIX #8] Track mounted state to prevent setTimeout firing after unmount
  const mountedRef = useRef(true);
  // Track timeout to debounce rapid tour selections and prevent duplicate dispatches
  const tourTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (tourTimeoutRef.current) clearTimeout(tourTimeoutRef.current);
    };
  }, []);

  const tours = userRole === 'doctor' ? doctorTours : attorneyTours;

  // [FIX #8] Use React state for completion status so the UI re-renders
  // when tours are reset. The old code called isTourCompleted() inline
  // during render (reading localStorage directly), so handleResetAll's
  // dispatched 'tours-reset' event was never listened to and the checkmarks
  // would not clear until a remount.
  const [completionMap, setCompletionMap] = useState<Record<string, boolean>>({});

  // Build completion map from localStorage whenever userId or tours change
  // [FIX #8] Also handles userId changing mid-session (re-login)
  useEffect(() => {
    if (!userId) {
      setCompletionMap({});
      return;
    }
    const map: Record<string, boolean> = {};
    for (const tour of tours) {
      map[tour.id] = isTourCompleted(tour.id, userId);
    }
    setCompletionMap(map);
  }, [userId, tours]);

  // Refresh completion map when a tour finishes (localStorage changes)
  // Listen for storage events and a custom refresh event
  useEffect(() => {
    if (!userId) return;

    const refresh = () => {
      if (!mountedRef.current) return;
      const map: Record<string, boolean> = {};
      for (const tour of tours) {
        map[tour.id] = isTourCompleted(tour.id, userId);
      }
      setCompletionMap(map);
    };

    // Listen for cross-tab storage changes
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('expertmd_tour_')) {
        refresh();
      }
    };

    // [FIX #3] All event listeners must have named references for proper cleanup
    let tourStartTimeoutId: ReturnType<typeof setTimeout>;
    const handleTourStart = () => {
      // Refresh after a short delay to allow the tour hook to update localStorage
      clearTimeout(tourStartTimeoutId);
      tourStartTimeoutId = setTimeout(refresh, 500);
    };

    window.addEventListener('storage', handleStorage);
    // Listen for same-tab tour completion or reset
    window.addEventListener('tours-reset', refresh);
    // Also refresh when a tour's driver.js destroy triggers completion
    window.addEventListener('start-product-tour', handleTourStart);

    return () => {
      clearTimeout(tourStartTimeoutId);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('tours-reset', refresh);
      window.removeEventListener('start-product-tour', handleTourStart);
    };
  }, [userId, tours]);

  const handleStartTour = useCallback(
    (tour: TourDefinition) => {
      // Clear any pending tour dispatch to debounce rapid selections
      if (tourTimeoutRef.current) clearTimeout(tourTimeoutRef.current);

      // [FIX #8] If onNavigate is not provided but tour.route exists,
      // log a warning and still fire the event -- the tour may still work
      // if the user is already on the correct page.
      const needsNavigation = !!(tour.route && onNavigate);
      if (needsNavigation) {
        onNavigate(tour.route!);
      }

      // Use a longer delay for cross-page navigation to account for lazy-loaded
      // routes that may take longer to render their tour target elements.
      // [FIX #8] Guard against firing after unmount.
      const delay = needsNavigation ? 2000 : 500;
      tourTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        const event = new CustomEvent('start-product-tour', {
          detail: { tourId: tour.id },
        });
        window.dispatchEvent(event);
      }, delay);
    },
    [onNavigate],
  );

  const handleResetAll = useCallback(() => {
    if (!userId) return;
    clearAllTours(tours, userId);
    // [FIX #8] Update React state directly so checkmarks clear immediately
    const cleared: Record<string, boolean> = {};
    for (const tour of tours) {
      cleared[tour.id] = false;
    }
    setCompletionMap(cleared);
    // Also dispatch event so any useProductTour hooks can reset their state
    window.dispatchEvent(new Event('tours-reset'));
  }, [tours, userId]);

  if (!userId) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-400 hover:text-purple-800 gap-1.5 font-medium"
          aria-label="Help & learning resources"
        >
          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-purple-500"></span>
          </span>
          <HelpCircle className="h-4 w-4" />
          <span className="hidden sm:inline text-xs">Help</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 bg-white border-gray-200 text-gray-900 shadow-lg"
      >
        {tours.map((tour) => {
          const completed = completionMap[tour.id] ?? false;
          return (
            <DropdownMenuItem
              key={tour.id}
              className="flex items-center justify-between cursor-pointer focus:bg-gray-100"
              onClick={() => handleStartTour(tour)}
            >
              <span className="text-sm text-gray-700">{tour.label}</span>
              {completed && <Check className="h-3.5 w-3.5 text-green-600" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator className="bg-gray-200" />
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer focus:bg-purple-50 py-2.5"
          onClick={() => window.open(getLearningCenterBaseUrl(), '_blank', 'noopener,noreferrer')}
        >
          <BookOpen className="h-4 w-4 text-purple-600" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">Learning Center</span>
            <span className="text-xs text-gray-500">Guides, tutorials & FAQs</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer focus:bg-gray-100"
          onClick={() =>
            window.open(getLearningCenterUrl('changelog'), '_blank', 'noopener,noreferrer')
          }
        >
          <Megaphone className="h-3.5 w-3.5 text-purple-600" />
          <span className="text-sm text-gray-700">What's New</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-gray-200" />
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer text-gray-500 focus:bg-gray-100 focus:text-gray-700"
          onClick={handleResetAll}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          <span className="text-sm">Reset All Tours</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
